/**
 * Shell Utility Functions (Module Federation Edition)
 * --------------------------------------------------
 * - Replaces all SystemJS logic with Module Federation remote loader
 * - Supports Vue 2 + Vue 3 MFEs
 * - Does NOT rely on shared Vue instances
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { useUserStore } from "../stores/userStore";
import type { LifeCycles, LifeCycleFn } from "single-spa";
import { shellReady } from "@/lib/config/spa/shell-ready";

/* ============================================================================
   DOM ELEMENT GETTER
   ============================================================================
   Creates and resolves a DOM element where the MFE will mount.
   Waits until the shell outlet exists before proceeding.
============================================================================ */

/**
 * Returns a function that resolves a DOM element for a given MFE.
 */
export function createDomElementGetter(
  appName: string
): () => Promise<HTMLElement> {
  return (): Promise<HTMLElement> =>
    new Promise<HTMLElement>((resolve) => {
      const getElement = (): void => {
        const outlet = document.getElementById("mfe-parent");

        // Retry until shell outlet is available
        if (!outlet) {
          setTimeout(getElement, 10);
          return;
        }

        // Clear previous MFE
        outlet.innerHTML = "";

        // Create new MFE mount node
        const mfeDiv = document.createElement("div");
        mfeDiv.id = `mfe-outlet-${appName}`;
        outlet.appendChild(mfeDiv);

        resolve(mfeDiv);
      };

      getElement();
    });
}

/* ============================================================================
   ROUTE MATCHER
   ============================================================================
   Utility to check whether current location matches a product route.
============================================================================ */

/**
 * Checks if the current URL matches the product route.
 */
export function matchProductRoute(
  location: Location,
  routeStartsWith: string
): boolean {
  return (
    location.pathname === `/${routeStartsWith}` ||
    location.pathname.startsWith(`/${routeStartsWith}/`)
  );
}

/* ============================================================================
   ACCESS CHECK
   ============================================================================
   Ensures user has access to the product before mounting the MFE.
   Redirects to preview page if access is missing.
============================================================================ */

/**
 * Validates product access before allowing mount.
 */
async function ensureProductAccess(
  productName: string,
  routeStartsWith: string
): Promise<boolean> {
  // Ensure shell initialization is complete
  await shellReady;

  const userStore = useUserStore();

  const isRouteMatch =
    window.location.pathname === `/${routeStartsWith}` ||
    window.location.pathname.startsWith(`/${routeStartsWith}/`);

  if (!isRouteMatch) return false;

  const hasAccess = userStore.getProductAccess(productName) && userStore.getProductAccessOnRoles(productName);

  if (!hasAccess) {
    const previewPath = `/preview`;

    // Soft navigation without reload
    window.history.pushState({}, "", previewPath);
    window.dispatchEvent(new PopStateEvent("popstate"));

    return false;
  }

  return true;
}

/* ============================================================================
   ACCESS GUARD WRAPPER
   ============================================================================
   Wraps a Single-SPA lifecycle with an access check.
============================================================================ */

/**
 * Wraps an MFE loader with an access guard.
 */
export function withAccessGuard(
  appLoader: () => Promise<LifeCycles<Record<string, unknown>>>,
  productName: string,
  routeStartsWith: string
): () => Promise<LifeCycles<Record<string, unknown>>> {
  return async (): Promise<LifeCycles<Record<string, unknown>>> => {
    const app = await appLoader();

    const originalMount = app.mount;

    // Normalize mount lifecycle into array (UNCHANGED)
    const mounts: LifeCycleFn<Record<string, unknown>>[] =
      Array.isArray(originalMount)
        ? originalMount
        : originalMount
        ? [originalMount]
        : [];

    return {
      ...app,

      async mount(props) {
        /**
         * ------------------------------------------------------------
         * MF COMPONENT RENDER BYPASS (NEW — SAFE)
         * ------------------------------------------------------------
         * This is ONLY set by the shell right before calling moduleEntry.
         * It does NOT affect normal navigation.
         */
        const isMfComponentRender =
          typeof window !== "undefined" &&
          window.history?.state?.__mfComponentRender === true;

        if (!isMfComponentRender) {
          const hasAccess = await ensureProductAccess(
            productName,
            routeStartsWith
          );

          if (!hasAccess) return Promise.resolve();
        }

        // ORIGINAL MOUNT FLOW (UNCHANGED)
        for (const mountFn of mounts) {
          await mountFn(props);
        }
      },
    };
  };
}


/* ============================================================================
   MODULE FEDERATION RUNTIME HELPERS
   ============================================================================
   Responsible for loading and initializing remote containers safely.
============================================================================ */

declare const __webpack_share_scopes__: {
  default: unknown;
};

/**
 * Cache to avoid reloading the same remote container.
 */
const containerCache: Map<string, WebpackContainer> = new Map();

/**
 * Minimal Webpack Module Federation container interface.
 */
interface WebpackContainer {
  init(shareScope: unknown): Promise<void>;
  get(module: string): Promise<() => LifeCycles<Record<string, unknown>>>;
}

/**
 * Loads a remoteEntry.js file as a classic script.
 */
function loadRemoteScript(url: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");

    script.src = url;
    script.type = "text/javascript";
    script.async = false; // IMPORTANT: Required for MF execution order
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`[MF Loader] Failed to load ${url}`));

    document.head.appendChild(script);
  });
}

/**
 * Loads and initializes a remote container.
 */
async function loadRemoteContainer(
  remoteBaseUrl: string,
  remoteName: string
): Promise<WebpackContainer> {
  if (containerCache.has(remoteName)) {
    return containerCache.get(remoteName)!;
  }

  const remoteEntryUrl = `${remoteBaseUrl}/remoteEntry.js?v=${Date.now()}`

  // Load remote entry script
  await loadRemoteScript(remoteEntryUrl);

  const container = (window as unknown as Record<string, WebpackContainer>)[
    remoteName
  ];

  if (!container) {
    throw new Error(
      `[MF Loader] Container '${remoteName}' not found on window`
    );
  }

  // Initialize share scope once
  await container.init(__webpack_share_scopes__.default);

  containerCache.set(remoteName, container);
  return container;
}

/* ============================================================================
   REMOTE LOADER WITH FALLBACK
   ============================================================================
   Loads a remote MFE safely and falls back to an unavailable route on failure.
============================================================================ */

/**
 * Loads an MFE with graceful failure handling.
 */
export function loadWithFallback(
  remoteBaseUrl: string,
  remoteName: string,
  productKey: string
): () => Promise<LifeCycles<Record<string, unknown>>> {
  return async (): Promise<LifeCycles<Record<string, unknown>>> => {
    try {
      // Notify shell: loading has started
      window.dispatchEvent(
        new CustomEvent("MFE_LOADED", {
          detail: true
        })
      );
      const container = await loadRemoteContainer(
        remoteBaseUrl,
        remoteName
      );

      const factory = await container.get("./Mount");
      return factory();
    } catch (error) {
      // Notify shell: loading failed / stop loader
      window.dispatchEvent(
        new CustomEvent("MFE_LOADED", {
          detail: false
        })
      );
      console.error(
        `[MFE Error] Failed to load ${productKey}`,
        error
      );

      // Redirect to unavailable page
      const unavailablePath = `/unavailable/${encodeURIComponent(
        productKey
      )}`;

      window.history.pushState({}, "", unavailablePath);
      window.dispatchEvent(new PopStateEvent("popstate"));

      // No-op lifecycle to keep single-spa stable
      return {
        bootstrap: async () => undefined,
        mount: async () => undefined,
        unmount: async () => undefined,
      };
    }
  };
}

/**
 * List of modules that include accountId in the URL structure.
 * Example paths:
 * /accounts/:accountId/...
 * /commerce/:accountId/...
 * /service/:accountId/...
 */
const ACCOUNT_MODULE_REGEX = /^\/(accounts|commerce|service)\/\d+/;

/**
 * Replaces the accountId in a given path if the path follows the pattern:
 *
 *  /accounts/:accountId/...
 *  /commerce/:accountId/...
 *  /service/:accountId/...
 *
 * Example:
 *  Input Path:  /commerce/25435/products
 *  New ID:      99999
 *
 *  Output:      /commerce/99999/products
 *
 * If the path does not match the expected structure, the original path is returned unchanged.
 *
 * @param path - Current route path (e.g., "/commerce/25435/products")
 * @param newAccountId - The accountId that should replace the existing one
 *
 * @returns Updated path with the new accountId
 */
export function getAccountSwitchPath(
  path: string,
  newAccountId: string | number
): string {
  if (!path) return path;

  return path.replace(
    ACCOUNT_MODULE_REGEX,
    `/$1/${newAccountId}`
  );
}


/**
 * Validates whether the current route belongs to the active account.
 *
 * Rules:
 * 1. If the path is root "/" (segments length = 0)
 *    → return true only if activeAccountId exists
 *
 * 2. If path contains less than 3 segments
 *    Example:
 *      /accounts/20001
 *      /service/20001
 *    → return false because a page after accountId is required
 *
 * 3. If path contains at least 3 segments
 *    Example:
 *      /accounts/20001/dashboard
 *      /service/20001/tickets
 *      /commerce/20001/products
 *
 *    → validate:
 *        - module is allowed
 *        - accountId matches activeAccountId
 *
 * @param path - current route path
 * @param activeAccountId - accountId currently stored in the application state
 *
 * @returns boolean
 *  true  → path belongs to active account
 *  false → invalid structure or account mismatch
 */
export function isActiveAccountPath(
  path: string,
  activeAccountId: string | null
): boolean {

  if (!path) return false;

  // Split path and remove empty segments
  const segments = path.split("/").filter(Boolean);

  /**
   * CASE 1
   * Root path "/"
   */
  if (segments.length === 0) {
    return !!activeAccountId;
  }

  /**
   * CASE 2
   * Invalid structure (must have at least 3 segments)
   *
   * Example invalid:
   * /accounts/20001
   * /service/20001
   */
  if (segments.length > 1 && segments.length < 3) {
    return false;
  }

  /**
   * CASE 3
   * Valid structure expected:
   * /module/accountId/page
   */
  const [module, accountId] = segments;

  const allowedModules = ["accounts", "service", "commerce"];

  if (!allowedModules.includes(module)) {
    return false;
  }

  if (!accountId || !activeAccountId) {
    return false;
  }

  return accountId === activeAccountId;
}